// Bannerin käyttöliittymä: ensimmäinen taso, toinen taso (kategoriavalinnat)
// ja tallennusvahvistus todistustunnuksineen.
//
// Saavutettavuus (V2-T10, WCAG 2.1 AA):
// - Banneri on modaali dialogi (role="dialog", aria-modal), jolla on
//   saavutettava nimi ja kuvaus (aria-labelledby / aria-describedby).
// - Näppäimistö: Tab ja Shift+Tab kiertävät vain dialogin sisällä (focus trap),
//   Esc palaa toiselta tasolta ensimmäiselle ja sulkee ensimmäisen tason.
//   Sulkeminen EI ole suostumus: mitään ei vapauteta eikä tallenneta.
// - Kategoriavalitsimet ovat role="switch" -painikkeita, joilla on
//   aria-checked ja kategorian nimen sisältävä saavutettava nimi.
// - Kohdistus siirtyy dialogiin avattaessa ja palautuu suljettaessa siihen
//   elementtiin, josta dialogi avattiin.
// - Fokusindikaattori piirretään omalla tyylisäännöllä, koska asiakassivuston
//   tyylit voivat poistaa selaimen oman indikaattorin.

import type { BannerColors, BannerTexts, CategoryConfig, Choices, CmpConfig, StoredConsent } from './types';

/**
 * Oletusvärit (V2-T10): kontrastit tarkistettu WCAG 2.1 AA -vaatimuksia vasten
 * (tavallinen teksti 4.5:1). Aiempi hyväksyntävihreä #16a34a antoi valkoisella
 * tekstillä 3.3:1 eli alitti vaatimuksen — oletus on nyt #15803d (5.0:1).
 * Asiakkaan omat väriteemat ovat asiakkaan vastuulla; oletusteeman on
 * läpäistävä tarkistus.
 */
export const DEFAULT_ACCEPT_BG = '#15803d';
export const DEFAULT_ACCEPT_TEXT = '#ffffff';
export const DEFAULT_REJECT_BG = '#e5e7eb';
export const DEFAULT_REJECT_TEXT = '#374151';
export const DEFAULT_CUSTOMIZE_TEXT = '#1d4ed8';
const FOCUS_STYLE_ID = 'cmp-a11y-styles';

/** Toimintopainikkeen tunniste: tyylisäännöt kohdistetaan vain näihin. */
const ACTION_BUTTON_ATTR = 'data-cmp-button';

/** Dialogin, otsikon, kuvauksen ja toimintorivin tunnisteet (V2, PL-25). */
const DIALOG_ATTR = 'data-cmp-dialog';
const TITLE_ATTR = 'data-cmp-title';
const DESC_ATTR = 'data-cmp-desc';
const ACTIONS_ATTR = 'data-cmp-actions';

/** Mobiilirajapyykki: tämän alle dialogi on lähes koko leveydellä. */
const MOBILE_MAX_WIDTH = 480;

/**
 * Bannerin tyylisäännöt (lisätään kertaalleen head-tagiin).
 *
 * Kaksi asiaa vaatii oikean tyylisäännön inline-tyylien sijaan:
 * 1. Fokusindikaattori — asiakassivuston tyylit voivat poistaa selaimen oman.
 * 2. Hover- ja painallustilat — niitä ei voi ilmaista inline-tyyleillä.
 *
 * Painikkeiden MUOTO (pehmuste, pyöristys, varjot, siirtymä) on täällä, jotta
 * se on pakostakin identtinen kaikilla toimintopainikkeilla. Vain VÄRIT
 * tulevat inline-tyyleistä sivuston teemasta, kuten ennenkin: hyväksy- ja
 * hylkää-nappi eroavat toisistaan ainoastaan väriltään (Traficom: hylkääminen
 * yhtä helppoa ja yhtä näkyvää kuin hyväksyminen).
 *
 * Tilat käyttävät vain box-shadowia, transformia ja filteriä — ei mitään, mikä
 * muuttaisi elementin kokoa tai sijaintia. Siksi hover ei aiheuta layout
 * shiftiä (V1-T8:n CLS-vartija).
 */
function ensureFocusStyles(): void {
  if (document.getElementById(FOCUS_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = FOCUS_STYLE_ID;
  const btn = '#cmp-overlay button[' + ACTION_BUTTON_ATTR + '="action"]';
  const dlg = '#cmp-overlay [' + DIALOG_ATTR + ']';
  style.textContent = [
    // Muoto ja perusvarjo: kevyt reunavarjo + pieni pudotusvarjo
    btn + '{padding:12px 22px;border:none;border-radius:10px;cursor:pointer;',
    'font-weight:600;font-size:14px;line-height:1.2;font-family:inherit;',
    'box-shadow:0 1px 2px rgba(0,0,0,0.06),0 2px 4px rgba(0,0,0,0.10);',
    'transition:box-shadow .15s ease,transform .15s ease,filter .15s ease}',
    // Hover: varjo syvenee ja nappi nousee pikselin — ei layout-muutosta
    '@media (hover:hover){' + btn + ':hover:not(:disabled){',
    'box-shadow:0 2px 4px rgba(0,0,0,0.08),0 4px 10px rgba(0,0,0,0.14);',
    'transform:translateY(-1px);filter:brightness(0.96)}}',
    // Painallus: nappi laskeutuu takaisin
    btn + ':active:not(:disabled){transform:translateY(0);',
    'box-shadow:0 1px 2px rgba(0,0,0,0.10)}',
    // Näppäimistöfokus: sama korostus kuin ennen, säilytetään sellaisenaan
    '#cmp-overlay button:focus-visible,#cmp-overlay [tabindex]:focus-visible{',
    'outline:3px solid #1d4ed8;outline-offset:2px}',
    '#cmp-overlay button:focus,#cmp-overlay [tabindex]:focus{',
    'outline:3px solid #1d4ed8;outline-offset:2px}',
    // Liikkumista välttävä kävijä: siirtymät ja nousu pois, varjot jäävät
    '@media (prefers-reduced-motion:reduce){' + btn + '{transition:none}',
    btn + ':hover:not(:disabled){transform:none}}',

    // --- Dialogin mitat ja typografia (PL-25) ---
    // Mitat ovat tyylisäännössä, jotta mobiilille voidaan antaa oma
    // mediakysely: inline-tyyleillä sitä ei voi tehdä. Värit tulevat edelleen
    // inline-tyyleistä sivuston teemasta.
    '#cmp-overlay{padding:16px;box-sizing:border-box}',
    dlg + '{width:100%;box-sizing:border-box;padding:30px;border-radius:14px;',
    'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;',
    'font-size:15px;line-height:1.65;box-shadow:0 4px 24px rgba(0,0,0,0.15)}',
    dlg + '[' + DIALOG_ATTR + '="box"]{max-width:680px}',
    dlg + '[' + DIALOG_ATTR + '="popup"]{max-width:460px}',
    dlg + '[' + DIALOG_ATTR + '="bar"]{max-width:100%}',
    // Asetusnäkymä on yhtä leveä kuin 1. taso ja vierii tarvittaessa
    dlg + '[' + DIALOG_ATTR + '="settings"]{max-width:680px;max-height:85vh;overflow-y:auto}',
    '#cmp-overlay [' + TITLE_ATTR + ']{margin:0 0 10px;font-size:21px;font-weight:700;line-height:1.3}',
    '#cmp-overlay [' + DESC_ATTR + ']{margin:0 0 24px;line-height:1.65;opacity:0.9}',
    '#cmp-overlay [' + ACTIONS_ATTR + ']{display:flex;flex-wrap:wrap;gap:10px}',
    '#cmp-overlay [' + ACTIONS_ATTR + '="settings"]{margin-top:24px}',

    // --- Mobiili: dialogi lähes koko leveydelle, painikkeet täysleveinä ---
    '@media (max-width:' + MOBILE_MAX_WIDTH + 'px){',
    '#cmp-overlay{padding:8px}',
    dlg + '{padding:20px;border-radius:12px;max-width:100%}',
    '#cmp-overlay [' + TITLE_ATTR + ']{font-size:19px}',
    '#cmp-overlay [' + DESC_ATTR + ']{margin-bottom:20px}',
    // Yksi nappi per rivi, koko leveydeltä — ei puolikkaita rivejä
    btn + '{flex:1 1 100%}}',
  ].join('');
  (document.head || document.documentElement).appendChild(style);
}

/** Dialogin fokusoitavat elementit dokumenttijärjestyksessä. */
function focusableIn(root: HTMLElement): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>('button:not([disabled]),[href],[tabindex]:not([tabindex="-1"])');
  return Array.from(nodes).filter(function (el) {
    return el.offsetParent !== null || el === document.activeElement;
  });
}

/**
 * Kytkee focus trapin ja Esc-käsittelyn dialogiin. Palauttaa purkufunktion.
 * onEscape saa päättää, mitä Esc tekee (paluu tasolle 1 tai sulkeminen).
 */
export function trapFocus(dialog: HTMLElement, onEscape: () => void): () => void {
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      onEscape();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = focusableIn(dialog);
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && (active === first || !dialog.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !dialog.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  }
  dialog.addEventListener('keydown', onKeyDown);
  return function () {
    dialog.removeEventListener('keydown', onKeyDown);
  };
}

/** UI:n tarvitsemat riippuvuudet — orkestrointi (cmp.ts) kytkee nämä. */
export interface BannerUiContext {
  config: CmpConfig;
  lang: string;
  getStoredConsent: () => StoredConsent | null;
  getConsentId: () => string | null;
  storeConsent: (choices: Choices, action: string) => void;
  applyConsent: (choices: Choices) => void;
  sendConsent: (choices: Choices, action: string, onDone?: (consentId: string) => void) => void;
}

/** Kaikki kategoriat hyväksyttynä. */
export function allAcceptedChoices(cats: CategoryConfig[]): Choices {
  const choices: Choices = {};
  cats.forEach(function (cat) { choices[cat.slug] = true; });
  return choices;
}

/** Vain välttämättömät hyväksyttynä (hylkää kaikki). */
export function requiredOnlyChoices(cats: CategoryConfig[]): Choices {
  const choices: Choices = {};
  cats.forEach(function (cat) { choices[cat.slug] = !!cat.is_required; });
  return choices;
}

/** Tallentaa, soveltaa ja lähettää valinnan sekä näyttää todistustunnuksen. */
function commitChoices(ctx: BannerUiContext, choices: Choices, action: string): void {
  ctx.storeConsent(choices, action);
  ctx.applyConsent(choices);
  ctx.sendConsent(choices, action, function (consentId) { showConfirmation(ctx, consentId); });
}

/**
 * Painike bannerin toimintoriville. Muoto (pehmuste, pyöristys, varjot, hover-
 * ja painallustilat) tulee yhteisestä tyylisäännöstä, joten se on identtinen
 * kaikilla toimintopainikkeilla molemmilla tasoilla; vain värit annetaan tässä.
 */
function actionButton(label: string | undefined, bg: string, color: string, onClick: () => void): HTMLButtonElement {
  ensureFocusStyles();
  const btn = createEl('button', { background: bg, color: color }, label) as HTMLButtonElement;
  btn.type = 'button';
  btn.setAttribute(ACTION_BUTTON_ATTR, 'action');
  btn.onclick = onClick;
  return btn;
}

/** Estää tuplaklikkaukset: harmaannuttaa kaikki napit lähetyksen ajaksi. */
function disableAllButtons(root: HTMLElement): void {
  const btns = root.querySelectorAll('button');
  for (let i = 0; i < btns.length; i++) {
    (btns[i] as HTMLButtonElement).disabled = true;
    (btns[i] as HTMLButtonElement).style.opacity = '0.6';
  }
}

/** Luo elementin, asettaa inline-tyylit ja tekstisisällön. */
export function createEl(tag: string, styles?: Partial<CSSStyleDeclaration>, text?: string): HTMLElement {
  const el = document.createElement(tag) as HTMLElement;
  if (styles) Object.assign(el.style, styles);
  if (text) el.textContent = text;
  return el;
}

/** Tallennusvahvistus: todistustunnus + kopiointi. Sulkeutuu 10 sekunnissa. */
export function showConfirmation(ctx: BannerUiContext, consentId: string): void {
  const found = document.getElementById('cmp-overlay');
  if (!found) return;
  const overlay: HTMLElement = found;
  const c: BannerColors = ctx.config.colors || {};

  overlay.innerHTML = '';
  ensureFocusStyles();
  const box = createEl('div', {
    background: c.background || '#fff', color: c.text || '#1a1a1a', borderRadius: '12px',
    padding: '24px', maxWidth: '480px', width: '100%', boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', fontSize: '14px',
    textAlign: 'center',
  });

  const mark = createEl('div', { fontSize: '28px', marginBottom: '12px' }, '✓');
  mark.setAttribute('aria-hidden', 'true');
  box.appendChild(mark);
  const confTitle = createEl('h2', { margin: '0 0 8px', fontSize: '18px', fontWeight: '700' }, 'Suostumuksesi on tallennettu');
  confTitle.id = 'cmp-confirm-title';
  box.appendChild(confTitle);
  const confDesc = createEl('p', { margin: '0 0 16px', lineHeight: '1.5', opacity: '0.8', fontSize: '13px' }, 'Voit käyttää alla olevaa todistustunnusta todisteena suostumuksestasi.');
  confDesc.id = 'cmp-confirm-desc';
  box.appendChild(confDesc);

  const idBox = createEl('div', {
    background: (c.text || '#1a1a1a') + '0a', borderRadius: '8px', padding: '12px',
    fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all', marginBottom: '16px',
    border: '1px solid ' + (c.divider || '#e5e7eb'),
  }, consentId);
  box.appendChild(idBox);

  const btnRow = createEl('div', { display: 'flex', gap: '8px', justifyContent: 'center' });
  const copyBtn = createEl('button', {
    padding: '10px 20px', border: '1px solid ' + (c.divider || '#e5e7eb'), borderRadius: '6px',
    background: 'transparent', color: c.text || '#1a1a1a', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
  }, 'Kopioi tunnus') as HTMLButtonElement;
  copyBtn.type = 'button';
  copyBtn.onclick = function () {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(consentId).then(function () { copyBtn.textContent = 'Kopioitu!'; });
    } else {
      const ta = document.createElement('textarea'); ta.value = consentId; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
      copyBtn.textContent = 'Kopioitu!';
    }
  };
  const closeBtn = createEl('button', {
    padding: '10px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer',
    fontWeight: '600', fontSize: '14px', background: c.acceptButtonBg || DEFAULT_ACCEPT_BG, color: c.acceptButtonText || DEFAULT_ACCEPT_TEXT,
  }, 'Sulje') as HTMLButtonElement;
  closeBtn.type = 'button';
  btnRow.appendChild(copyBtn);
  btnRow.appendChild(closeBtn);
  box.appendChild(btnRow);

  // Vahvistus on oma dialogi: nimi, kuvaus, fokus ja Esc-sulkeminen
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'cmp-confirm-title');
  overlay.setAttribute('aria-describedby', 'cmp-confirm-desc');
  overlay.appendChild(box);

  const releaseTrap = trapFocus(overlay, function () { closeOverlay(); });
  let closed = false;
  function closeOverlay() {
    if (closed) return;
    closed = true;
    releaseTrap();
    overlay.remove();
    restoreFocus();
  }
  closeBtn.onclick = closeOverlay;
  closeBtn.focus();

  setTimeout(function () {
    if (document.getElementById('cmp-overlay') === overlay) closeOverlay();
  }, 10000);
}

/** Elementti, josta banneri avattiin — kohdistus palautetaan tänne. */
let _openerElement: HTMLElement | null = null;

function rememberOpener(): void {
  const active = document.activeElement as HTMLElement | null;
  if (active && active !== document.body) _openerElement = active;
}

function restoreFocus(): void {
  const opener = _openerElement;
  _openerElement = null;
  if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
    opener.focus();
  }
}

/** Bannerin ensimmäinen taso: otsikko, kuvaus ja toimintopainikkeet. */
export function renderBanner(ctx: BannerUiContext): void {
  const CONFIG = ctx.config;
  const LANG = ctx.lang;
  const texts = CONFIG.texts[LANG] || CONFIG.texts['fi'] || CONFIG.texts[Object.keys(CONFIG.texts)[0]];
  if (!texts) return;
  const c: BannerColors = CONFIG.colors || {};
  const cats = CONFIG.categories || [];

  ensureFocusStyles();
  rememberOpener();

  // HUOM: reunuksen (padding) on oltava tyylisäännössä eikä inline-tyylissä,
  // koska inline-tyyli voittaa mediakyselyn — mobiilin kapeampi reunus ei
  // muuten astuisi voimaan (PL-25).
  const overlay = createEl('div', {
    position: 'fixed', top: '0', left: '0', right: '0', bottom: '0',
    background: c.overlayBg || 'rgba(0,0,0,0.5)', zIndex: '999998', display: 'flex',
    alignItems: CONFIG.position === 'top' ? 'flex-start' : CONFIG.position === 'center' ? 'center' : 'flex-end',
    justifyContent: 'center',
  });
  overlay.id = 'cmp-overlay';
  // Modaali dialogi: apuvälineet lukevat otsikon ja kuvauksen, eivätkä
  // vaella taustasivun sisältöön (aria-modal).
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'cmp-title');
  overlay.setAttribute('aria-describedby', 'cmp-desc');

  // Leveys tulee tyylisäännöstä asettelutyylin mukaan (PL-25); inline-tyyleihin
  // jäävät vain teeman värit.
  const layout = CONFIG.layout_style === 'bar' ? 'bar' : CONFIG.layout_style === 'popup' ? 'popup' : 'box';
  const box = createEl('div', {
    background: c.background || '#fff', color: c.text || '#1a1a1a',
  });
  box.setAttribute(DIALOG_ATTR, layout);

  const existingConsentId = ctx.getConsentId();
  if (existingConsentId) {
    const idNotice = createEl('div', {
      background: (c.text || '#1a1a1a') + '0a', borderRadius: '6px', padding: '8px 12px',
      fontSize: '12px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px',
      border: '1px solid ' + (c.divider || '#e5e7eb'),
    });
    const short = existingConsentId.length > 16 ? existingConsentId.substring(0, 16) + '...' : existingConsentId;
    idNotice.appendChild(createEl('span', { opacity: '0.7' }, 'Todistustunnus: ' + short));
    const cpBtn = createEl('button', {
      padding: '2px 8px', border: '1px solid ' + (c.divider || '#e5e7eb'), borderRadius: '4px',
      background: 'transparent', color: c.text || '#1a1a1a', cursor: 'pointer', fontSize: '11px',
    }, 'Kopioi') as HTMLButtonElement;
    cpBtn.type = 'button';
    cpBtn.setAttribute('aria-label', 'Kopioi todistustunnus');
    cpBtn.onclick = function () {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(existingConsentId).then(function () { cpBtn.textContent = 'Kopioitu!'; });
      } else {
        const ta = document.createElement('textarea'); ta.value = existingConsentId; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
        cpBtn.textContent = 'Kopioitu!';
      }
    };
    idNotice.appendChild(cpBtn);
    box.appendChild(idNotice);
  }

  const title = createEl('h2', {}, texts.first_layer_title);
  title.id = 'cmp-title';
  title.setAttribute(TITLE_ATTR, '');
  const desc = createEl('p', {}, texts.first_layer_description);
  desc.id = 'cmp-desc';
  desc.setAttribute(DESC_ATTR, '');
  box.appendChild(title);
  box.appendChild(desc);

  const btnRow = createEl('div', {});
  btnRow.setAttribute(ACTIONS_ATTR, 'first');

  let _sending = false;
  function guard(fn: () => void) {
    return function () {
      if (_sending) return;
      _sending = true;
      disableAllButtons(overlay);
      fn();
    };
  }

  const acceptBtn = actionButton(
    texts.accept_all_button, c.acceptButtonBg || DEFAULT_ACCEPT_BG, c.acceptButtonText || DEFAULT_ACCEPT_TEXT,
    guard(function () { commitChoices(ctx, allAcceptedChoices(cats), 'accept_all'); }),
  );
  btnRow.appendChild(acceptBtn);

  if (CONFIG.show_reject_on_first_layer) {
    btnRow.appendChild(actionButton(
      texts.reject_all_button, c.rejectButtonBg || DEFAULT_REJECT_BG, c.rejectButtonText || DEFAULT_REJECT_TEXT,
      guard(function () { commitChoices(ctx, requiredOnlyChoices(cats), 'reject_all'); }),
    ));
  }

  btnRow.appendChild(actionButton(texts.customize_button, c.customizeButtonBg || 'transparent', c.customizeButtonText || DEFAULT_CUSTOMIZE_TEXT, function () {
    releaseTrap();
    showSecondLayer(ctx, overlay, cats, texts, c);
  }));

  box.appendChild(btnRow);
  overlay.appendChild(box);
  document.body.appendChild(overlay);

  // Esc sulkee bannerin. Sulkeminen EI ole suostumus: mitään ei tallenneta
  // eikä vapauteta, ja banneri palaa seuraavalla sivulatauksella.
  const releaseTrap = trapFocus(overlay, function () {
    releaseTrap();
    overlay.remove();
    restoreFocus();
  });
  // Kohdistus dialogiin: ensimmäinen toimintopainike (ei automaattista
  // hyväksyntää — kohdistus ei aktivoi mitään).
  acceptBtn.focus();
}

/** Bannerin toinen taso: kategoriakohtaiset valinnat ja evästelistat. */
export function showSecondLayer(
  ctx: BannerUiContext,
  overlay: HTMLElement,
  cats: CategoryConfig[],
  texts: BannerTexts,
  c: BannerColors,
): void {
  const LANG = ctx.lang;
  overlay.innerHTML = '';
  ensureFocusStyles();
  // Sama ilmavuus kuin 1. tasolla; mitat tyylisäännöstä (PL-25)
  const box = createEl('div', {
    background: c.background || '#fff', color: c.text || '#1a1a1a',
  });
  box.setAttribute(DIALOG_ATTR, 'settings');

  const secondTitle = createEl('h2', {}, texts.second_layer_title);
  secondTitle.id = 'cmp-title';
  secondTitle.setAttribute(TITLE_ATTR, '');
  box.appendChild(secondTitle);
  const secondDesc = createEl('p', {}, texts.second_layer_description);
  secondDesc.id = 'cmp-desc';
  secondDesc.setAttribute(DESC_ATTR, '');
  box.appendChild(secondDesc);

  const stored = ctx.getStoredConsent();
  const storedChoices = stored && stored.choices ? stored.choices : null;
  console.log('[CMP] Second layer opened, restoring choices:', storedChoices);
  const states: Choices = {};
  cats.forEach(function (cat) {
    if (cat.is_required) {
      states[cat.slug] = true;
    } else if (storedChoices && Object.prototype.hasOwnProperty.call(storedChoices, cat.slug)) {
      states[cat.slug] = !!storedChoices[cat.slug];
    } else {
      states[cat.slug] = !!cat.is_default_enabled;
    }
    const catTexts = cat.texts[LANG] || cat.texts['fi'] || cat.texts[Object.keys(cat.texts)[0]] || {};

    const row = createEl('div', { padding: '16px 0', borderBottom: '1px solid ' + (c.divider || '#e5e7eb') });
    const header = createEl('div', { display: 'flex', justifyContent: 'space-between', alignItems: 'center' });
    const left = createEl('div', {});
    left.appendChild(createEl('div', { fontWeight: '600' }, catTexts.name || cat.slug));
    left.appendChild(createEl('div', { fontSize: '13px', opacity: '0.8', marginTop: '4px' }, catTexts.description || ''));
    header.appendChild(left);

    if (cat.is_required) {
      // Kontrasti: 0.5 läpinäkyvyys jäi alle AA-vaatimuksen, 0.7 riittää
      header.appendChild(createEl('span', { fontSize: '12px', opacity: '0.7' }, 'Pakollinen'));
    } else {
      const toggle = createEl('button', {
        width: '44px', height: '24px', borderRadius: '12px', cursor: 'pointer',
        // Reunaviiva: kytkimen ääriviivan on erotuttava taustasta myös
        // pois-tilassa (WCAG 1.4.11, ei-tekstin kontrasti 3:1)
        border: '1px solid rgba(0,0,0,0.45)',
        background: states[cat.slug] ? (c.toggleOn || DEFAULT_ACCEPT_BG) : (c.toggleOff || '#d1d5db'),
        position: 'relative', transition: 'background 0.2s', flexShrink: '0',
      }) as HTMLButtonElement;
      toggle.type = 'button';
      toggle.setAttribute('role', 'switch');
      toggle.setAttribute('aria-checked', states[cat.slug] ? 'true' : 'false');
      toggle.setAttribute('aria-label', catTexts.name || cat.slug);
      const knob = createEl('div', {
        width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px',
        left: states[cat.slug] ? '22px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      });
      knob.setAttribute('aria-hidden', 'true');
      toggle.appendChild(knob);
      toggle.onclick = (function (slug: string, toggleEl: HTMLElement, knobEl: HTMLElement) {
        return function () {
          states[slug] = !states[slug];
          toggleEl.style.background = states[slug] ? (c.toggleOn || DEFAULT_ACCEPT_BG) : (c.toggleOff || '#d1d5db');
          toggleEl.setAttribute('aria-checked', states[slug] ? 'true' : 'false');
          knobEl.style.left = states[slug] ? '22px' : '2px';
        };
      })(cat.slug, toggle, knob);
      header.appendChild(toggle);
    }
    row.appendChild(header);

    if (cat.cookies && cat.cookies.length > 0) {
      const table = createEl('div', {
        marginTop: '10px', fontSize: '12px', lineHeight: '1.5', background: (c.text || '#1a1a1a') + '06',
        borderRadius: '8px', padding: '10px', overflow: 'auto',
      });
      const thRow = createEl('div', { display: 'flex', gap: '12px', fontWeight: '600', opacity: '0.75', paddingBottom: '4px', borderBottom: '1px solid ' + (c.divider || '#e5e7eb'), marginBottom: '4px' });
      thRow.appendChild(createEl('div', { minWidth: '100px', flex: '1' }, 'Nimi'));
      thRow.appendChild(createEl('div', { minWidth: '100px', flex: '1' }, 'Tarjoaja'));
      thRow.appendChild(createEl('div', { minWidth: '60px' }, 'Kesto'));
      table.appendChild(thRow);
      cat.cookies.forEach(function (ck) {
        const tr = createEl('div', { display: 'flex', gap: '12px', padding: '3px 0', opacity: '0.9' });
        tr.appendChild(createEl('div', { minWidth: '100px', flex: '1', fontFamily: 'monospace', fontSize: '11px', wordBreak: 'break-all' }, ck.name));
        // Tarjoajan nimi voi olla pitkä katkeamaton isäntänimi
        // (esim. connect.facebook.net) — ilman rivinvaihtoa se ylivuotaisi
        // sarakkeestaan kapealla näytöllä seuraavan sarakkeen päälle.
        tr.appendChild(createEl('div', { minWidth: '100px', flex: '1', wordBreak: 'break-word' }, ck.provider));
        tr.appendChild(createEl('div', { minWidth: '60px' }, ck.duration));
        table.appendChild(tr);
        const purpose = (ck.purpose && (ck.purpose[LANG] || ck.purpose['fi'])) || '';
        if (purpose) {
          const pRow = createEl('div', { paddingLeft: '0', opacity: '0.75', fontSize: '11px', paddingBottom: '4px' });
          pRow.textContent = purpose;
          table.appendChild(pRow);
        }
      });
      row.appendChild(table);
    }

    box.appendChild(row);
  });

  // Toimintorivi: "Hyväksy kaikki" ja "Hylkää kaikki" ovat saatavilla myös
  // tällä tasolla (V2-T4) — kävijän ei tarvitse palata takaisin tehdäkseen
  // kokonaisvalinnan. Napit ovat keskenään samanmuotoiset ja -kokoiset.
  const btnRow = createEl('div', {});
  btnRow.setAttribute(ACTIONS_ATTR, 'settings');

  let _saving = false;
  function guard(fn: () => void) {
    return function () {
      if (_saving) return;
      _saving = true;
      disableAllButtons(overlay);
      fn();
    };
  }

  btnRow.appendChild(actionButton(
    texts.accept_all_button, c.acceptButtonBg || DEFAULT_ACCEPT_BG, c.acceptButtonText || DEFAULT_ACCEPT_TEXT,
    guard(function () { commitChoices(ctx, allAcceptedChoices(cats), 'accept_all'); }),
  ));
  btnRow.appendChild(actionButton(
    texts.reject_all_button, c.rejectButtonBg || DEFAULT_REJECT_BG, c.rejectButtonText || DEFAULT_REJECT_TEXT,
    guard(function () { commitChoices(ctx, requiredOnlyChoices(cats), 'reject_all'); }),
  ));
  // "Tallenna valinnat" pitää vaihe 1:n muotoilun: kävijän omat valinnat eivät
  // saa näyttää vähemmän houkuttelevalta vaihtoehdolta kuin "Hyväksy kaikki".
  btnRow.appendChild(actionButton(
    texts.save_preferences_button, c.acceptButtonBg || DEFAULT_ACCEPT_BG, c.acceptButtonText || DEFAULT_ACCEPT_TEXT,
    guard(function () { commitChoices(ctx, states, 'save_preferences'); }),
  ));
  box.appendChild(btnRow);

  overlay.appendChild(box);

  // Esc palaa asetusnäkymästä ensimmäiselle tasolle (ei sulje banneria):
  // kävijä ei putoa umpikujaan vaan takaisin kokonaisvalintoihin.
  const releaseTrap = trapFocus(overlay, function () {
    releaseTrap();
    overlay.remove();
    renderBanner(ctx);
  });

  // Kohdistus dialogiin itseensä, ei painikkeeseen (WAI-ARIA:n dialogikäytäntö).
  // Syy on konkreettinen: asetusnäkymä vierii kapealla näytöllä, ja
  // painikkeeseen kohdistaminen vierittäisi säiliön alas heti avattaessa —
  // kävijä ei näkisi otsikkoa eikä sitä, mistä hän on valitsemassa. Näin
  // ruudunlukija lukee dialogin nimen ja kuvauksen, ja Tab siirtyy
  // ensimmäiseen kytkimeen. tabindex="-1" ei lisää dialogia Tab-kiertoon.
  overlay.setAttribute('tabindex', '-1');
  box.scrollTop = 0;
  overlay.focus();
}
